import _debounce from "lodash/debounce";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Input, Label, Modal, Search, TextArea } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import { useSearchInterventionElements } from "components/InterventionSubitemsModal/components/KeyloopSubitemsModal/hooks";
import "components/InterventionSubitemsModal/components/KeyloopSubitemsModal/KeyloopSubitemsModal.scss";
import { ACL_ACTIVITIES, ACL_RESOURCES, INTERVENTION_ELEMENT_TYPES } from "models";
import { InterventionElementIcon } from "modules/AppointmentDetails/components/Interventions/components/Subitems/components";
import { convertPascalCaseToText } from "utils/common";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { DealerLocation, InterventionElement } from "models";
import type { SearchResultData } from "semantic-ui-react";

type KeyloopSubitemsModalProps = {
  isLoading?: boolean;
  element?: InterventionElement | null;
  location?: DealerLocation;
  onSubmit: (element: InterventionElement) => void;
  onClose?: () => void;
};

const DEBOUNCE_TIME = 500;
const MIN_SEARCH_LENGTH = 4;

export const KeyloopSubitemsModal = ({ onClose, onSubmit, element, location, isLoading }: KeyloopSubitemsModalProps) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState("");
  const [selectedElement, setSelectedElement] = useState<InterventionElement | null>(element || null);
  const [isSearching, setIsSearching] = useState(false);
  const searchFieldRef = useRef<HTMLDivElement>(null);
  const canSearchInterventionElements = useCan(ACL_ACTIVITIES.Search, ACL_RESOURCES.InterventionElementsCodes);

  const focusSearchInput = () => {
    const input = searchFieldRef.current?.querySelector("input");
    if (input) {
      input.focus();
      input.click();
    }
  };

  const { data: interventionElements, isFetching, refetch } = useSearchInterventionElements({ searchTerm: searchValue, dealerLocationId: location?.id || 0 });

  const debouncedSearch = useMemo(() => _debounce(() => refetch(), DEBOUNCE_TIME), [refetch]);

  const searchResults = (interventionElements ?? []).map(element => ({
    id: element.id,
    title: `${element.reference_code} - ${element.brand_code} - ${element.description}`,
    element
  }));

  const handleSearchChange = (_: React.MouseEvent, { value }: { value?: string }) => {
    setSearchValue(value || "");
    setSelectedElement(null);
    if (value && value.length >= MIN_SEARCH_LENGTH) debouncedSearch();
  };

  const handleResultSelect = (_: React.MouseEvent, data: SearchResultData) => {
    setSelectedElement(data.result.element);
    setIsSearching(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSearching(true);
    focusSearchInput();
  };

  const handleSubmit = () => {
    if (!selectedElement) return;

    onSubmit({
      ...selectedElement,
      dms_nr: element?.dms_nr || `claire_${formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)}`,
      created_on: element?.created_on || formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      is_local: true,
      labor_minutes: selectedElement.labor_minutes || null,
      parent_dms_nr: selectedElement.parent_dms_nr || "",
      id: element?.id ?? -1,
      intervention_id: element?.intervention_id ?? -1,
      vat: element?.vat ?? null
    });
  };

  const getElementTypeName = (elementType: INTERVENTION_ELEMENT_TYPES) => {
    const name = Object.entries(INTERVENTION_ELEMENT_TYPES).find(([_, value]) => value === elementType)?.[0];
    return name ? convertPascalCaseToText(name) : "";
  };

  return (
    <Modal open size="tiny" onClose={onClose} className="KeyloopSubitemsModal">
      <Modal.Header>{element ? t("v8_edit_subitem", "Edit subitem") : t("v8_add_subitem", "Add Subitem")}</Modal.Header>
      <Modal.Content>
        <Form id="keyloop-subitem">
          <Form.Field className="search-field" ref={searchFieldRef}>
            <label>{t("v8_search", "Search")}</label>
            <Search
              fluid
              input={{ disabled: !canSearchInterventionElements }}
              value={searchValue}
              results={searchResults}
              loading={isFetching}
              minCharacters={MIN_SEARCH_LENGTH}
              icon={<Icon className="magnifying glass" />}
              onSearchChange={handleSearchChange}
              onResultSelect={handleResultSelect}
              onBlur={() => setIsSearching(false)}
              resultRenderer={({ element: { element_type, reference_code, brand_code, description } }) => (
                <div className="element-search-result">
                  <Label className="element-search-icon">
                    <InterventionElementIcon elementType={element_type} />
                  </Label>
                  <span>{`${reference_code} - ${brand_code} - ${description}`}</span>
                </div>
              )}
              noResultsMessage={t("v8_no_results", "No results found")}
              placeholder={t("v8_keyloop_search_subitem", "Search by name, part number or brand")}
            />
            {!isSearching && selectedElement && (
              <div className="selected-value" onMouseDown={e => e.preventDefault()} onClick={handleOverlayClick}>
                <Label className="element-search-icon">
                  <InterventionElementIcon elementType={selectedElement.element_type} />
                </Label>
                <span>{`${selectedElement.reference_code} - ${selectedElement.brand_code} - ${selectedElement.description}`}</span>
              </div>
            )}
          </Form.Field>

          <>
            <Form.Field>
              <label>{t("v8_type", "Type")}</label>
              <div className="type-input-readonly">
                {selectedElement?.element_type ? (
                  <>
                    <Label className="element-type-icon">
                      <InterventionElementIcon elementType={selectedElement.element_type} />
                    </Label>
                    <span>{getElementTypeName(selectedElement.element_type)}</span>
                  </>
                ) : (
                  <span className="placeholder">{t("v8_type", "Type")}</span>
                )}
              </div>
            </Form.Field>

            <Form.Field>
              <label>{t("v8_brand", "Brand")}</label>
              <Input className="keyloop-subitem-input" fluid readOnly value={selectedElement?.brand_code || ""} placeholder={t("v8_brand", "Brand")} />
            </Form.Field>

            <Form.Field>
              <label>{t("v8_code", "Code")}</label>
              <Input className="keyloop-subitem-input" fluid readOnly value={selectedElement?.reference_code || ""} placeholder={t("v8_code", "Code")} />
            </Form.Field>

            <Form.Field>
              <label>{t("v8_description", "Description")}</label>
              <TextArea
                className="keyloop-subitem-input"
                readOnly
                value={selectedElement?.description || ""}
                placeholder={`${t("v8_description", "Description")}`}
                rows={2}
              />
            </Form.Field>
          </>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <div className="action-buttons">
          <Button className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button color="green" type="button" onClick={handleSubmit} loading={isLoading} disabled={isLoading || !selectedElement}>
            {t("v8_add", "Add")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
